/** Корзина: строки с количеством, телефон, адрес и оформление.
 *
 * Правила лимитов живут на сервере — здесь они лишь вежливо повторяются
 * подсказками, чтобы человек не узнавал о них в момент отказа. Остатков
 * клиент не видит: степпер просто не идёт выше потолка, а потолок ниже
 * общего объясняется словами «больше добавить нельзя».
 *
 * Телефон и адрес — обязательные спутники заказа: телефон помнится навсегда,
 * адресов может быть несколько, прошлые подсказываются чипами.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, request } from '@/shared/api';
import type { CartInfo, CartLine, Catalog } from '@/shared/types';
import { openManager, tap } from '@/shared/tg';
import { count } from '@/shared/plural';
import { PHONE_FULL, shapePhone } from '@/shared/phone';
import { MapPick, type PickedPoint } from './MapPick';
import {
  BagGlyph, BoltGlyph, CartGlyph, CheckGlyph, CloseGlyph, GiftGlyph,
  MinusGlyph, PinGlyph, PlusGlyph, TrashGlyph,
} from './icons';
import type { Reward } from '@/shared/types';
import { groupRewards } from './screens';

/** Ключ текущей отправки: пока заявка не подтверждена, повтор идёт с ним же. */
let pendingOrderKey: string | null = null;

/** Черновик оформления живёт на модуле: сходил в каталог за ещё одним
    товаром — телефон, адрес, комментарий и выбранные бонусы на месте. */
let checkoutDraft: {
  phone?: string;
  address?: string;
  comment?: string;
  express?: boolean;
  geo?: PickedPoint | null;
  cutId?: number | null;
  giftId?: number | null;
} | null = null;

function orderKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

const STORE = 'parbet-cart-v1';

/** Ключ позиции: товар и вкус кончаются порознь. */
const keyOf = (line: { product_id: string; variant?: string }) =>
  `${line.product_id}${(line.variant ?? '').trim()}`;

function readStore(): CartLine[] {
  try {
    const kept = JSON.parse(localStorage.getItem(STORE) ?? '[]');
    return Array.isArray(kept) ? kept.filter((line) => line?.product_id) : [];
  } catch {
    return [];
  }
}

export interface CartApi {
  lines: CartLine[];
  count: number;
  /** Потолок позиции: общий или складской, если тот ниже */
  maxFor: (productId: string, variant?: string) => number;
  /** Сколько уже лежит этой позиции */
  inCart: (productId: string, variant?: string) => number;
  add: (line: CartLine) => void;
  setQty: (line: CartLine, qty: number) => void;
  clear: () => void;
  info: CartInfo;
}

export function useCart(info: CartInfo): CartApi {
  const [lines, setLines] = useState<CartLine[]>(readStore);

  // Снапшот уезжает на сервер с передышкой: он нужен для напоминания
  // забывшему, а не для синхронизации каждого тапа
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    localStorage.setItem(STORE, JSON.stringify(lines));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      request('/api/cart/save', { lines }).catch(() => undefined);
    }, 1200);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [lines]);

  const caps = useMemo(() => {
    const map = new Map<string, number>();
    for (const cap of info.caps) {
      map.set(keyOf({ product_id: cap.product_id, variant: cap.variant }), cap.max);
    }
    return map;
  }, [info.caps]);

  const maxFor = useCallback(
    (productId: string, variant = '') =>
      caps.get(keyOf({ product_id: productId, variant })) ?? info.cap,
    [caps, info.cap],
  );

  const inCart = useCallback(
    (productId: string, variant = '') =>
      lines.find((line) => keyOf(line) === keyOf({ product_id: productId, variant }))
        ?.qty ?? 0,
    [lines],
  );

  const setQty = useCallback((line: CartLine, qty: number) => {
    setLines((previous) => {
      // По месту, а не «вынуть и положить в конец»: иначе строка прыгает
      if (qty <= 0) {
        return previous.filter((kept) => keyOf(kept) !== keyOf(line));
      }
      return previous.map((kept) =>
        keyOf(kept) === keyOf(line) ? { ...kept, qty } : kept);
    });
  }, []);

  const add = useCallback((line: CartLine) => {
    setLines((previous) => {
      const known = previous.find((kept) => keyOf(kept) === keyOf(line));
      if (!known) return [...previous, line];
      return previous.map((kept) =>
        keyOf(kept) === keyOf(line) ? { ...kept, qty: kept.qty + line.qty } : kept,
      );
    });
  }, []);

  const clear = useCallback(() => setLines([]), []);

  return useMemo(
    () => ({
      lines,
      count: lines.reduce((sum, line) => sum + line.qty, 0),
      maxFor,
      inCart,
      add,
      setQty,
      clear,
      info,
    }),
    [lines, maxFor, inCart, add, setQty, clear, info],
  );
}

// ------------------------------------------------------------------ экран

const money = (raw: string) => {
  const value = Number(String(raw).replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : null;
};



/** Шаг количества: две крупные цели и число между ними.
 *
 * Пальцу на ходу нужен запас: кнопки 40px при строке 64px — это ещё не
 * кнопка-баннер, но уже не промахнёшься.
 */
function Stepper({
  qty,
  max,
  onChange,
  onCeiling,
}: {
  qty: number;
  max: number;
  onChange: (qty: number) => void;
  onCeiling: () => void;
}) {
  const bump = (delta: number) => {
    const next = qty + delta;
    if (next > max) {
      tap('error');
      onCeiling();
      return;
    }
    tap();
    onChange(Math.max(0, next));
  };
  const cap = qty >= max;
  return (
    <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-glass p-1">
      <button
        type="button"
        aria-label="Убавить"
        onClick={() => bump(-1)}
        className="grid size-9 place-items-center rounded-full text-frost transition-colors duration-150 active:bg-glass-active"
      >
        <MinusGlyph size={16} />
      </button>
      <span className="min-w-6 text-center text-[15px] font-[700]" data-numeric>
        {qty}
      </span>
      <button
        type="button"
        aria-label="Прибавить"
        onClick={() => bump(1)}
        className={`grid size-9 place-items-center rounded-full transition-colors duration-150 active:bg-glass-active ${cap ? 'text-hint/40' : 'text-frost'}`}
      >
        <PlusGlyph size={16} />
      </button>
    </span>
  );
}

/** Заголовок раздела корзины: воздух сверху больше, чем снизу. */
function Section({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-6 mb-2 text-[15px] font-[680] tracking-[-.01em]">{children}</h2>
  );
}

/** Строка итога: подпись слева, число справа, одна сетка на все строки. */
function Line({
  label,
  value,
  tone,
  strong,
}: {
  label: string;
  value: string;
  tone?: 'hint' | 'gold';
  strong?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-3 ${strong ? 'text-[17px] font-[720]' : 'text-[13.5px]'} ${tone === 'gold' ? 'text-gold-2' : tone === 'hint' ? 'text-hint' : ''}`}
    >
      <span className="min-w-0 truncate">{label}</span>
      <span className="shrink-0" data-numeric>
        {value}
      </span>
    </div>
  );
}

/** Ячейка награды в опциях заказа: скидка и подарок — одна и та же строка,
    различаются содержимым и подсказкой.

    Тап по пустой ячейке всегда раскрывает список, даже когда бонус один:
    молчаливый выбор единственного читался как «ничего не произошло», а
    человек должен увидеть, ЧТО он применяет, прежде чем оно применится. */
function RewardSlot({
  chosen,
  pool,
  open,
  mark,
  empty,
  hintFor,
  onBare,
  onOpen,
}: {
  chosen: Reward | null;
  pool: Reward[];
  /** Список этой ячейки раскрыт — строка обязана это показывать */
  open: boolean;
  /** Что в кружке слева, пока ничего не выбрано */
  mark: React.ReactNode;
  empty: string;
  hintFor: (picked: Reward) => string;
  onBare: (next: Reward | null) => void;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        tap();
        if (chosen) onBare(null);
        else onOpen();
      }}
      className={`flex w-full items-center gap-3 rounded-card px-3.5 py-3 text-left transition-colors duration-150 ${
        chosen
          ? 'border border-gold/40 bg-gold/12'
          : open
            ? 'glass border-gold/40'
            : 'glass wet-edge'
      }`}
    >
      <span
        className={`grid size-9 shrink-0 place-items-center rounded-full text-[15px] font-[700] ${chosen || open ? 'bg-gold/20 text-gold-2' : 'bg-glass-2 text-hint'}`}
      >
        {mark}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-[14px] font-[650] ${chosen ? 'text-gold-2' : ''}`}>
          {chosen ? chosen.title : empty}
        </span>
        <span className="block text-[12.5px] text-hint">
          {chosen
            ? hintFor(chosen)
            : open
              ? 'Выберите из списка ниже'
              : count(groupRewards(pool).length, 'бонус', 'бонуса', 'бонусов') +
                ' в кошельке'}
        </span>
      </span>
      <span className={`shrink-0 text-[12px] ${open && !chosen ? 'text-gold-2' : 'text-hint'}`}>
        {chosen ? 'убрать' : open ? 'скрыть' : 'выбрать'}
      </span>
    </button>
  );
}

/** Список бонусов ячейки. Живёт сразу под своей строкой, а не под всеми
    опциями: раскрытый у скидки, но нарисованный под подарками, он читался
    как список подарков. Одинаковые бонусы — одной строкой со счётчиком. */
function RewardList({
  pool,
  onPick,
}: {
  pool: Reward[];
  onPick: (next: Reward | null) => void;
}) {
  return (
    <div className="animate-fade grid max-h-[264px] gap-1.5 overflow-y-auto overscroll-contain pl-3">
      {groupRewards(pool).map(({ sample, ids }) => (
        <button
          key={sample.id}
          type="button"
          onClick={() => {
            tap();
            onPick(pool.find((kept) => kept.id === ids[0]) ?? null);
          }}
          className="glass rounded-xl px-3.5 py-3 text-left text-[13.5px] transition-colors duration-150 active:bg-glass-active"
        >
          {sample.title}
          {ids.length > 1 && <span className="text-hint"> ×{ids.length}</span>}
        </button>
      ))}
    </div>
  );
}

export function CartScreen({
  cart,
  catalog,
  rewards = [],
  onDone,
  onToast,
  onStale,
  onCatalog,
}: {
  cart: CartApi;
  catalog: Catalog;
  /** Кошелёк: одна награда на заказ, применяется на оформлении */
  rewards?: Reward[];
  onDone: (orderId: number) => void;
  onToast: (text: string) => void;
  /** Витрина отстала от склада — перечитать каталог со свежими потолками */
  onStale?: () => void;
  /** Пустая корзина отправляет в каталог, а не в тупик */
  onCatalog?: () => void;
}) {
  const [phone, setPhone] = useState(() => checkoutDraft?.phone ?? (cart.info.phone || '+375 '));
  // Пустым: прошлый адрес, подставленный молча, уезжал с заказом, для
  // которого он неверен. Прошлые — чипами ниже, выбор осознанный
  const [address, setAddress] = useState(() => checkoutDraft?.address ?? '');
  const [express, setExpress] = useState(() => checkoutDraft?.express ?? false);
  const [comment, setComment] = useState(() => checkoutDraft?.comment ?? '');
  const [geo, setGeo] = useState<PickedPoint | null>(() => checkoutDraft?.geo ?? null);
  const [mapOpen, setMapOpen] = useState(false);
  // Наград к заказу две ячейки: скидка и подарок. Правило сервера, фронт
  // его повторяет: складываются только разные виды, два процента — нет
  const [cut, setCut] = useState<Reward | null>(
    () => rewards.find((r) => r.id === checkoutDraft?.cutId) ?? null,
  );
  const [gift, setGift] = useState<Reward | null>(
    () => rewards.find((r) => r.id === checkoutDraft?.giftId) ?? null,
  );
  const [picking, setPicking] = useState<'cut' | 'gift' | null>(null);
  const [sending, setSending] = useState(false);
  /** Чем сервер недоволен: поле или позиция подсвечивается */
  const [trouble, setTrouble] = useState<{ code: string; productId?: string } | null>(null);
  /** Корзина отстала от склада: чистим целиком и говорим нейтрально —
      что именно кончилось, клиенту знать не обязательно */
  const [refreshed, setRefreshed] = useState(false);

  /** Пока человек печатает, липкая полоска уходит: на маленьком экране с
      поднятой клавиатурой она садится ровно на поле ввода. */
  const [typing, setTyping] = useState(false);
  /** Кнопка в потоке видна — дублировать её полоской незачем. */
  const ctaRef = useRef<HTMLButtonElement>(null);
  const [ctaSeen, setCtaSeen] = useState(false);
  useEffect(() => {
    const node = ctaRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const watch = new IntersectionObserver(
      ([entry]) => setCtaSeen(Boolean(entry?.isIntersecting)),
      { rootMargin: '-40px 0px -90px 0px' },
    );
    watch.observe(node);
    return () => watch.disconnect();
  }, [cart.lines.length]);

  const sorted = useMemo(
    () =>
      [...cart.lines].sort(
        (a, b) =>
          a.title.localeCompare(b.title, 'ru') ||
          (a.variant ?? '').localeCompare(b.variant ?? '', 'ru'),
      ),
    [cart.lines],
  );

  // Кошелёк по полочкам: проценты в одну, вещи и экспресс в другую
  const cuts = useMemo(() => rewards.filter((r) => r.kind === 'percent'), [rewards]);
  const gifts = useMemo(() => rewards.filter((r) => r.kind !== 'percent'), [rewards]);
  /** Экспресс уже в подарке: платный не предлагается — дарёное не продают */
  const expressGift = gift?.kind === 'express';

  const goods = useMemo(
    () =>
      cart.lines.reduce((sum, line) => {
        const price = money(line.price);
        return price === null ? sum : sum + price * line.qty;
      }, 0),
    [cart.lines],
  );
  const priceless = cart.lines.some((line) => money(line.price) === null);
  const expressPrice =
    express && !expressGift && cart.info.express ? cart.info.express.price : 0;
  // Скидка процентом — к товарам, не к доставке: так же посчитает сервер
  const discount = cut?.value ? Math.round(goods * cut.value) / 100 : 0;
  const total = goods - discount + expressPrice;
  const money2 = (value: number) => `${Math.round(value * 100) / 100} BYN`;

  // Сколько паркоинов принесёт заказ — считает сервер теми же правилами, что
  // начислят после подтверждения. Пересчёт с задержкой: не дёргать его на
  // каждом «+1»
  const [quote, setQuote] = useState<{ enabled: boolean; coins: number } | null>(null);

  // Черновик пишется на каждое изменение: экран размонтируется при уходе на
  // другую вкладку, и без этого набранное пропадало
  useEffect(() => {
    checkoutDraft = {
      phone, address, comment, express, geo,
      cutId: cut?.id ?? null, giftId: gift?.id ?? null,
    };
  }, [phone, address, comment, express, geo, cut, gift]);
  useEffect(() => {
    if (cart.lines.length === 0) {
      setQuote(null);
      return;
    }
    let alive = true;
    const timer = setTimeout(() => {
      request<{ enabled: boolean; coins: number }>('/api/cart/quote', {
        lines: cart.lines,
        reward_ids: [cut?.id].filter((id): id is number => id != null),
      })
        .then((answer) => {
          if (alive) setQuote(answer);
        })
        .catch(() => {
          if (alive) setQuote(null);
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [cart.lines, cut?.id]);
  const promised = quote?.enabled && quote.coins > 0 ? quote.coins : 0;

  const PHONE_OK = PHONE_FULL.test(phone);
  const addressOk = address.trim().length > 0;

  // Ключ повтора живёт на модуле: ответ мог не дойти, человек жмёт снова —
  // сервер вернёт ту же заявку, а не создаст вторую
  const submit = async () => {
    if (!PHONE_OK || !addressOk) {
      tap('error');
      setTrouble({ code: !PHONE_OK ? 'phone' : 'address' });
      document
        .getElementById(!PHONE_OK ? 'cart-phone' : 'cart-address')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return;
    }
    setSending(true);
    setTrouble(null);
    try {
      if (!pendingOrderKey) pendingOrderKey = orderKey();
      const result = await request<{ ok: boolean; order_id: number }>('/api/cart/order', {
        lines: cart.lines,
        phone,
        address: address.trim(),
        express,
        reward_ids: [cut?.id, gift?.id].filter((id): id is number => id != null),
        comment: comment.trim(),
        geo: geo ? { lat: geo.lat, lon: geo.lon } : null,
        order_key: pendingOrderKey,
      });
      pendingOrderKey = null;
      checkoutDraft = null;
      cart.clear();
      tap('success');
      onDone(result.order_id);
    } catch (error) {
      tap('error');
      const api = error as ApiError;
      const code = String(api.body?.error ?? '');
      if (code === 'unavailable') {
        // Убираем только то, что выпало: сервер называет позиции, а не
        // остатки. Остальное человек не собирает заново
        const gone = (api.body?.items as { product_id: string; variant?: string }[] | undefined) ?? [];
        const dropped = cart.lines.filter((line) =>
          gone.some(
            (item) => item.product_id === line.product_id && (item.variant ?? '') === (line.variant ?? ''),
          ),
        );
        if (dropped.length && dropped.length < cart.lines.length) {
          for (const line of dropped) cart.setQty(line, 0);
          onToast(`Убрали из корзины: ${dropped.map((line) => line.title).join(', ')} — закончилось`);
        } else {
          cart.clear();
          setRefreshed(true);
        }
        pendingOrderKey = null;
        onStale?.();
      } else if (code === 'not_saved') {
        onToast('Заявка не записалась — попробуйте ещё раз');
      } else if (code === 'wholesale' || code === 'not_enough') {
        setTrouble({ code, productId: String(api.body?.product_id ?? '') });
      } else if (code === 'phone' || code === 'address') {
        setTrouble({ code });
      } else if (code === 'too_fast') {
        // Сервер держит частоту заявок: человеку — подождать, а не звонить
        onToast('Слишком часто. Подождите немного и отправьте снова');
      } else {
        onToast(`Не отправилось: ${api.message}`);
      }
    } finally {
      setSending(false);
    }
  };

  const staleDialog = refreshed ? (
    <div className="fixed inset-0 z-50 grid place-items-center bg-night/70 p-6 backdrop-blur-[3px]">
      <div className="glass wet-edge w-full max-w-[300px] rounded-layer p-5 text-center">
        <span className="mx-auto mb-3 grid size-12 place-items-center rounded-full bg-mint/12 text-mint">
          <CartGlyph size={24} />
        </span>
        <p className="text-[15px] font-[680]">Наличие обновилось</p>
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-hint">
          Соберите корзину заново — в каталоге уже всё свежее.
        </p>
        <button
          type="button"
          onClick={() => {
            tap();
            setRefreshed(false);
            onCatalog?.();
          }}
          className="mt-4 w-full rounded-xl bg-linear-135 from-mint to-mint-deep py-3 text-[14px] font-[680] text-mint-ink transition-transform duration-150 active:scale-[.98]"
        >
          В каталог
        </button>
      </div>
    </div>
  ) : null;

  if (cart.lines.length === 0) {
    return (
      <>
        <div className="animate-fade grid place-items-center gap-3 py-16 text-center">
          <span className="grid size-16 place-items-center rounded-full bg-glass text-hint">
            <CartGlyph size={30} />
          </span>
          <p className="text-[16px] font-[680]">Корзина пуста</p>
          <p className="max-w-[240px] text-[13.5px] leading-relaxed text-hint">
            Добавьте товары из каталога — количество и вкусы выбираются в
            карточке.
          </p>
          {onCatalog && (
            <button
              type="button"
              onClick={() => {
                tap();
                onCatalog();
              }}
              className="mt-1 rounded-xl bg-linear-135 from-mint to-mint-deep px-6 py-3 text-[14px] font-[680] text-mint-ink transition-transform duration-150 active:scale-[.98]"
            >
              Открыть каталог
            </button>
          )}
        </div>
        {staleDialog}
      </>
    );
  }

  const field =
    'w-full rounded-xl border bg-glass px-4 py-3 text-base text-frost outline-none transition-colors duration-150 placeholder:text-hint focus:border-mint focus:bg-glass-2';

  return (
    <div className="animate-fade pb-2">
      {staleDialog}

      <div className="mb-2.5 flex items-center gap-2">
        <span className="min-w-0 flex-1 text-[12.5px] text-hint">
          {count(cart.lines.length, 'позиция', 'позиции', 'позиций')} ·{' '}
          <span data-numeric>{cart.count}</span> шт.
        </span>
        <ClearCartButton onClear={() => cart.clear()} />
      </div>

      <div className="grid gap-2">
        {sorted.map((line) => {
          const price = money(line.price);
          const max = cart.maxFor(line.product_id, line.variant);
          const sore = trouble?.productId === line.product_id;
          return (
            <div
              key={keyOf(line)}
              className={`glass wet-edge rounded-card p-3 ${sore ? 'border-danger' : ''}`}
            >
              <div className="flex items-start gap-3">
                {line.thumb ? (
                  <img
                    src={line.thumb}
                    alt=""
                    className="size-[52px] shrink-0 rounded-thumb object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span className="grid size-[52px] shrink-0 place-items-center rounded-thumb bg-glass-2 text-hint">
                    <BagGlyph size={22} />
                  </span>
                )}

                <div className="min-w-0 flex-1">
                  <p className="text-[14px] leading-tight font-[650] break-words">
                    {line.title}
                  </p>
                  {line.variant && (
                    <p className="mt-0.5 text-[12.5px] leading-tight text-mint break-words">
                      {line.variant}
                    </p>
                  )}
                  <p className="mt-0.5 text-[12px] text-hint" data-numeric>
                    {price !== null ? `${price} BYN за шт.` : line.price}
                  </p>
                </div>

                <button
                  type="button"
                  aria-label="Убрать позицию"
                  onClick={() => {
                    tap();
                    cart.setQty(line, 0);
                  }}
                  className="-mt-1 -mr-1 grid size-9 shrink-0 place-items-center rounded-full text-hint transition-colors duration-150 active:bg-glass-active"
                >
                  <TrashGlyph size={17} />
                </button>
              </div>

              <div className="mt-2.5 flex items-center justify-between gap-3">
                <Stepper
                  qty={line.qty}
                  max={max}
                  onChange={(qty) => cart.setQty(line, qty)}
                  onCeiling={() =>
                    onToast(
                      max < cart.info.cap
                        ? 'Больше добавить нельзя'
                        : `До ${cart.info.cap} шт. Для оптовых заказов свяжитесь с менеджером`,
                    )
                  }
                />
                <span className="text-[15px] font-[700]" data-numeric>
                  {price !== null ? money2(price * line.qty) : '—'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {(trouble?.code === 'wholesale' || trouble?.code === 'not_enough') && (
        <div className="animate-fade mt-2 rounded-card bg-danger/12 px-3.5 py-2.5 text-[13px] text-danger">
          {trouble.code === 'wholesale'
            ? 'В заказе слишком много штук одной позиции. Для оптовых заказов свяжитесь с менеджером.'
            : 'По отмеченной позиции столько заказать не получится — уменьшите количество.'}
          {trouble.code === 'wholesale' && (
            <button
              type="button"
              onClick={() => openManager(catalog.manager_url)}
              className="mt-1.5 block font-[650] underline"
            >
              Написать менеджеру
            </button>
          )}
        </div>
      )}

      {(cart.info.express || rewards.length > 0) && (
        <>
          <Section>Опции заказа</Section>
          <div className="grid gap-2">
            {cart.info.express && (
              <button
                type="button"
                disabled={expressGift}
                onClick={() => {
                  tap();
                  setExpress((was) => !was);
                }}
                className={`glass wet-edge flex w-full items-center gap-3 rounded-card px-3.5 py-3 text-left transition-colors duration-150 ${express && !expressGift ? 'border-mint' : ''} ${expressGift ? 'opacity-60' : ''}`}
              >
                <span
                  className={`grid size-9 shrink-0 place-items-center rounded-full transition-colors duration-150 ${express && !expressGift ? 'bg-mint/15 text-mint' : 'bg-glass-2 text-hint'}`}
                >
                  <BoltGlyph size={19} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-[650]">
                    {cart.info.express.title}
                  </span>
                  <span className="block text-[12.5px] text-hint">
                    {expressGift
                      ? 'Уже в подарке — платить не нужно'
                      : `Быстрее обычной · +${cart.info.express.price} BYN`}
                  </span>
                </span>
                <span
                  className={`grid size-6 shrink-0 place-items-center rounded-full border transition-colors duration-150 ${express && !expressGift ? 'border-mint bg-mint text-mint-ink' : 'border-glass-line'}`}
                >
                  {express && !expressGift && <CheckGlyph size={13} />}
                </span>
              </button>
            )}

            {cuts.length > 0 && (
              <>
                <RewardSlot
                  chosen={cut}
                  pool={cuts}
                  open={picking === 'cut'}
                  mark="%"
                  empty="Применить скидку"
                  hintFor={() => 'Скидка учтена в итоге'}
                  onBare={(next) => {
                    setCut(next);
                    setPicking(null);
                  }}
                  onOpen={() => setPicking((was) => (was === 'cut' ? null : 'cut'))}
                />
                {picking === 'cut' && (
                  <RewardList
                    pool={cuts}
                    onPick={(next) => {
                      setCut(next);
                      setPicking(null);
                    }}
                  />
                )}
              </>
            )}
            {gifts.length > 0 && (
              <>
                <RewardSlot
                  chosen={gift}
                  pool={gifts}
                  open={picking === 'gift'}
                  mark={<GiftGlyph size={19} />}
                  empty="Выбрать подарок"
                  hintFor={(picked) =>
                    picked.kind === 'express'
                      ? 'Доставка экспрессом — бесплатно'
                      : 'Приедет вместе с заказом'
                  }
                  onBare={(next) => {
                    setGift(next);
                    setPicking(null);
                    if (next?.kind === 'express') setExpress(false);
                  }}
                  onOpen={() => setPicking((was) => (was === 'gift' ? null : 'gift'))}
                />
                {picking === 'gift' && (
                  <RewardList
                    pool={gifts}
                    onPick={(next) => {
                      setGift(next);
                      setPicking(null);
                      if (next?.kind === 'express') setExpress(false);
                    }}
                  />
                )}
              </>
            )}
          </div>
        </>
      )}

      <Section>Доставка</Section>

      <label className="mb-1 block text-[12px] text-hint" htmlFor="cart-phone">
        Телефон для связи
      </label>
      <input
        id="cart-phone"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={phone}
        onFocus={() => setTyping(true)}
        onBlur={() => setTyping(false)}
        onChange={(event) => {
          setPhone(shapePhone(event.target.value));
          if (trouble?.code === 'phone') setTrouble(null);
        }}
        placeholder="+375 XX XXXXXXX"
        className={`${field} ${trouble?.code === 'phone' ? 'border-danger' : 'border-glass-line'}`}
        data-numeric
      />
      {trouble?.code === 'phone' && (
        <p className="mt-1 text-[12.5px] text-danger">
          Нужен номер целиком: +375, код и семь цифр.
        </p>
      )}

      <label className="mt-3 mb-1 block text-[12px] text-hint" htmlFor="cart-address">
        Адрес доставки
      </label>
      <div className="relative">
        <textarea
          id="cart-address"
          value={address}
          onFocus={() => setTyping(true)}
          onBlur={() => setTyping(false)}
          onChange={(event) => {
            setAddress(event.target.value);
            if (trouble?.code === 'address') setTrouble(null);
          }}
          placeholder="Улица, дом, квартира"
          rows={2}
          className={`${field} resize-none pr-11 ${trouble?.code === 'address' ? 'border-danger' : 'border-glass-line'}`}
        />
        {address && (
          <button
            type="button"
            aria-label="Очистить адрес"
            onClick={() => {
              tap();
              setAddress('');
            }}
            className="absolute top-2 right-2 grid size-8 place-items-center rounded-full bg-glass-2 text-hint transition-colors duration-150 active:bg-glass-active"
          >
            <CloseGlyph size={15} />
          </button>
        )}
      </div>
      {trouble?.code === 'address' && (
        <p className="mt-1 text-[12.5px] text-danger">
          Укажите адрес или поставьте точку на карте.
        </p>
      )}

      {cart.info.addresses.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {cart.info.addresses
            .filter((known) => known.trim() !== address.trim())
            .slice(0, 4)
            .map((known) => (
              <button
                key={known}
                type="button"
                onClick={() => {
                  tap();
                  setAddress(known);
                }}
                className="max-w-full truncate rounded-full border border-glass-line bg-glass px-3 py-2 text-[12.5px] text-hint transition-colors duration-150 active:bg-glass-active"
              >
                {known}
              </button>
            ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          tap();
          setMapOpen(true);
        }}
        className={`mt-2 flex w-full items-center gap-3 rounded-card px-3.5 py-3 text-left transition-colors duration-150 ${geo ? 'border border-mint/40 bg-mint/12' : 'glass wet-edge'}`}
      >
        <span
          className={`grid size-9 shrink-0 place-items-center rounded-full ${geo ? 'bg-mint/20 text-mint' : 'bg-glass-2 text-hint'}`}
        >
          <PinGlyph size={19} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-[14px] font-[650] ${geo ? 'text-mint' : ''}`}>
            {geo ? 'Точка на карте выбрана' : 'Указать точку на карте'}
          </span>
          <span className="block text-[12.5px] text-hint">
            {geo ? 'Курьер приедет по ней' : 'Точнее адреса, если дом найти сложно'}
          </span>
        </span>
        {geo && (
          <span
            role="button"
            aria-label="Убрать точку"
            onClick={(event) => {
              event.stopPropagation();
              tap();
              setGeo(null);
            }}
            className="grid size-8 shrink-0 place-items-center rounded-full text-hint"
          >
            <CloseGlyph size={15} />
          </span>
        )}
      </button>

      <label className="mt-3 mb-1 block text-[12px] text-hint" htmlFor="cart-comment">
        Комментарий · необязательно
      </label>
      <textarea
        id="cart-comment"
        value={comment}
        onFocus={() => setTyping(true)}
        onBlur={() => setTyping(false)}
        onChange={(event) => setComment(event.target.value)}
        placeholder="Пожелания"
        rows={2}
        className={`${field} resize-none border-glass-line`}
      />

      {mapOpen && (
        <MapPick
          start={geo}
          onClose={() => setMapOpen(false)}
          onDone={(point) => {
            setMapOpen(false);
            setGeo(point);
            // Адрес переписывается всегда: старый текст с новой точкой — это
            // ровно тот случай, когда менеджер поедет не туда
            setAddress(
              point.label ??
                `Точка на карте: ${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`,
            );
          }}
        />
      )}

      <Section>Итого</Section>
      <div className="glass wet-edge grid gap-2 rounded-card p-4">
        <Line
          label={`Товары · ${cart.count} шт.`}
          value={goods > 0 ? money2(goods) : '—'}
          tone="hint"
        />
        {expressPrice > 0 && (
          <Line
            label={cart.info.express?.title ?? 'Экспресс'}
            value={`+${money2(expressPrice)}`}
            tone="hint"
          />
        )}
        {expressGift && (
          <Line
            label={`${cart.info.express?.title ?? 'Экспресс'} — подарок`}
            value="бесплатно"
            tone="gold"
          />
        )}
        {discount > 0 && (
          <Line label="Скидка по бонусу" value={`−${money2(discount)}`} tone="gold" />
        )}
        {promised > 0 && (
          <Line
            label="Паркоинов за этот заказ"
            value={`+${count(promised, 'паркоин', 'паркоина', 'паркоинов')}`}
            tone="gold"
          />
        )}
        <div className="mt-1 border-t border-glass-line pt-2.5">
          <Line
            label="К оплате"
            value={total > 0 ? (priceless ? `от ${money2(total)}` : money2(total)) : '—'}
            strong
          />
        </div>
        {priceless && (
          <p className="text-[12px] leading-relaxed text-hint">
            По части позиций цену подтвердит менеджер — итог может измениться.
          </p>
        )}
      </div>

      <button
        ref={ctaRef}
        type="button"
        disabled={sending}
        onClick={submit}
        className="mt-3 w-full rounded-xl bg-linear-135 from-mint to-mint-deep py-4 text-[16px] font-[680] text-mint-ink shadow-[0_6px_18px_rgba(43,217,196,.22)] transition-transform duration-150 active:scale-[.98] disabled:opacity-60"
      >
        {sending ? 'Отправляем…' : 'Оформить заказ'}
      </button>
      <p className="mt-2 mb-1 text-center text-[12px] leading-relaxed text-hint">
        Менеджер подтвердит заказ в чате и привезёт по адресу.
      </p>

      {/* Полоска с итогом — только пока кнопка не видна и человек не печатает:
          на маленьком экране с клавиатурой она садилась ровно на поле ввода */}
      <div
        className={`fixed inset-x-0 bottom-[var(--above-tabs)] z-20 px-3.5 transition-[transform,opacity] duration-200 ease-glass ${
          ctaSeen || typing
            ? 'pointer-events-none translate-y-3 opacity-0'
            : 'translate-y-0 opacity-100'
        }`}
      >
        <div className="mx-auto flex max-w-[520px] items-center gap-3 rounded-layer border border-glass-line bg-night-2/[.92] p-2 pl-4 backdrop-blur-[16px] layer-shadow">
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-[720]" data-numeric>
              {total > 0 ? (priceless ? `от ${money2(total)}` : money2(total)) : '—'}
            </span>
            <span className="block text-[12px] text-hint" data-numeric>
              {cart.count} шт.
              {promised > 0 && ` · +${promised} П`}
            </span>
          </span>
          <button
            type="button"
            onClick={() => {
              tap();
              ctaRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }}
            className="shrink-0 rounded-xl bg-linear-135 from-mint to-mint-deep px-5 py-3 text-[14px] font-[680] text-mint-ink"
          >
            К оформлению
          </button>
        </div>
      </div>
    </div>
  );
}

/** «Очистить корзину» — с передумыванием вместо всплывашки: первый тап
    спрашивает, второй, в течение трёх секунд, чистит. */
function ClearCartButton({ onClear }: { onClear: () => void }) {
  const [arming, setArming] = useState(false);
  useEffect(() => {
    if (!arming) return;
    const timer = setTimeout(() => setArming(false), 3000);
    return () => clearTimeout(timer);
  }, [arming]);

  return (
    <button
      type="button"
      onClick={() => {
        tap(arming ? 'error' : undefined);
        if (arming) {
          onClear();
          setArming(false);
        } else {
          setArming(true);
        }
      }}
      className={`flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[12.5px] font-[680] transition-colors duration-150 ${arming ? 'bg-danger text-night' : 'bg-danger/12 text-danger active:bg-danger/25'}`}
    >
      <TrashGlyph size={14} />
      {arming ? 'Точно? Нажмите ещё' : 'Очистить'}
    </button>
  );
}
